import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const MINUTES_IN_DAY = 24 * 60;

/**
 * Last-resort timezone, mirroring the backend's `DEFAULT_COMPANY_TIMEZONE`. Deliberately a real
 * US zone rather than UTC — every tenant is a US rental operator, so UTC would silently shift
 * every business by 4-8 hours.
 */
export const DEFAULT_COMPANY_TIMEZONE = "America/New_York";

/**
 * Approximate US state -> IANA timezone map, kept in sync with
 * `softcar-backend/src/app/modules/global/company/utils/timezone.ts` and the booking frontend's
 * `src/lib/locationWallClock.ts`. Only a fallback for companies without an explicit `timezone`.
 */
const US_STATE_TIMEZONES = {
    Alabama: "America/Chicago",
    Alaska: "America/Anchorage",
    Arizona: "America/Phoenix",
    Arkansas: "America/Chicago",
    California: "America/Los_Angeles",
    Colorado: "America/Denver",
    Connecticut: "America/New_York",
    Delaware: "America/New_York",
    "District Of Columbia": "America/New_York",
    Florida: "America/New_York",
    Georgia: "America/New_York",
    Hawaii: "Pacific/Honolulu",
    Idaho: "America/Denver",
    Illinois: "America/Chicago",
    Indiana: "America/New_York",
    Iowa: "America/Chicago",
    Kansas: "America/Chicago",
    Kentucky: "America/New_York",
    Louisiana: "America/Chicago",
    Maine: "America/New_York",
    Maryland: "America/New_York",
    Massachusetts: "America/New_York",
    Michigan: "America/New_York",
    Minnesota: "America/Chicago",
    Mississippi: "America/Chicago",
    Missouri: "America/Chicago",
    Montana: "America/Denver",
    Nebraska: "America/Chicago",
    Nevada: "America/Los_Angeles",
    "New Hampshire": "America/New_York",
    "New Jersey": "America/New_York",
    "New Mexico": "America/Denver",
    "New York": "America/New_York",
    "North Carolina": "America/New_York",
    "North Dakota": "America/Chicago",
    Ohio: "America/New_York",
    Oklahoma: "America/Chicago",
    Oregon: "America/Los_Angeles",
    Pennsylvania: "America/New_York",
    "Rhode Island": "America/New_York",
    "South Carolina": "America/New_York",
    "South Dakota": "America/Chicago",
    Tennessee: "America/Chicago",
    Texas: "America/Chicago",
    Utah: "America/Denver",
    Vermont: "America/New_York",
    Virginia: "America/New_York",
    Washington: "America/Los_Angeles",
    "West Virginia": "America/New_York",
    Wisconsin: "America/Chicago",
    Wyoming: "America/Denver",
};

const US_COUNTRY_NAMES = new Set(["united states", "united states of america", "usa", "us"]);

/** @param {string | null | undefined} value */
export const isValidTimeZone = (value) => {
    if (!value) return false;
    try {
        // Constructed purely to let Intl reject an unknown zone for us.
        Intl.DateTimeFormat("en-US", { timeZone: value });
        return true;
    } catch {
        return false;
    }
};

/**
 * Full state name -> postal abbreviation. Address rows hold both forms: the current onboarding UI
 * writes full names ("New York"), older records hold abbreviations ("NY", "ny", "DC"). Matching
 * only full names silently returns null for every legacy row.
 */
const STATE_NAME_TO_ABBR = {
    Alabama: "AL",
Alaska: "AK",
Arizona: "AZ",
Arkansas: "AR",
California: "CA",
    Colorado: "CO",
Connecticut: "CT",
Delaware: "DE",
"District Of Columbia": "DC",
    Florida: "FL",
Georgia: "GA",
Hawaii: "HI",
Idaho: "ID",
Illinois: "IL",
Indiana: "IN",
    Iowa: "IA",
Kansas: "KS",
Kentucky: "KY",
Louisiana: "LA",
Maine: "ME",
Maryland: "MD",
    Massachusetts: "MA",
Michigan: "MI",
Minnesota: "MN",
Mississippi: "MS",
Missouri: "MO",
    Montana: "MT",
Nebraska: "NE",
Nevada: "NV",
"New Hampshire": "NH",
"New Jersey": "NJ",
    "New Mexico": "NM",
"New York": "NY",
"North Carolina": "NC",
"North Dakota": "ND",
    Ohio: "OH",
Oklahoma: "OK",
Oregon: "OR",
Pennsylvania: "PA",
"Rhode Island": "RI",
    "South Carolina": "SC",
"South Dakota": "SD",
Tennessee: "TN",
Texas: "TX",
Utah: "UT",
    Vermont: "VT",
Virginia: "VA",
Washington: "WA",
"West Virginia": "WV",
Wisconsin: "WI",
    Wyoming: "WY",
};

const US_ABBR_TIMEZONES = Object.entries(US_STATE_TIMEZONES).reduce((acc, [name, zone]) => {
    const abbr = STATE_NAME_TO_ABBR[name];
    if (abbr) acc[abbr] = zone;
    return acc;
}, {});

/** @param {{ state?: string, country?: string } | null | undefined} address */
const timeZoneFromAddress = (address) => {
    if (!address) return null;

    const country = address.country?.trim().toLowerCase();
    if (country && !US_COUNTRY_NAMES.has(country)) return null;

    const state = address.state?.trim();
    if (!state) return null;

    if (state.length === 2) return US_ABBR_TIMEZONES[state.toUpperCase()] ?? null;

    const matched = Object.keys(US_STATE_TIMEZONES).find(
        (name) => name.toLowerCase() === state.toLowerCase(),
    );
    return matched ? US_STATE_TIMEZONES[matched] : null;
};

/**
 * The timezone a company's bookings and operational hours are expressed in.
 *
 * Every place the dashboard renders a reservation instant must go through this rather than the
 * admin's own browser timezone — otherwise the same booking reads differently for a manager in
 * Dhaka than for one at the branch itself.
 *
 * @param {{ timezone?: string, address?: { state?: string, country?: string } } | null | undefined} company
 */
export const resolveCompanyTimeZone = (company) => {
    if (isValidTimeZone(company?.timezone)) return company.timezone;

    const derived = timeZoneFromAddress(company?.address);
    if (isValidTimeZone(derived)) return derived;

    return DEFAULT_COMPANY_TIMEZONE;
};

/**
 * The clock a booking's times are enforced on: the pickup location's own `timezone`, its state,
 * the company's `timezone`, then the company address's state. Mirrors the backend's
 * `resolvePickupTimeZoneStrict` and the booking frontend's `resolveBookingTimeZoneStrict`.
 *
 * Returns null rather than guessing, because the server skips hours enforcement in exactly that
 * case — gating on a default zone would block times the server accepts.
 */
export const resolveBookingTimeZoneStrict = (company, location) => {
    if (isValidTimeZone(location?.timezone)) return location.timezone;

    const fromLocation = timeZoneFromAddress(location);
    if (isValidTimeZone(fromLocation)) return fromLocation;

    if (isValidTimeZone(company?.timezone)) return company.timezone;

    const fromAddress = timeZoneFromAddress(company?.address);
    if (isValidTimeZone(fromAddress)) return fromAddress;

    return null;
};

/** Like `resolveBookingTimeZoneStrict`, but always answers — for anchoring picks and rendering. */
export const resolveBranchTimeZone = (company, location) =>
    resolveBookingTimeZoneStrict(company, location) ?? DEFAULT_COMPANY_TIMEZONE;

const isValidMinutes = (value) =>
    typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= MINUTES_IN_DAY;

/**
 * Reads wall-clock digits out of a legacy `Date` operational-hours value.
 *
 * Legacy values were never real instants — the dashboard wrote "9:00 AM" as
 * `dayjs.utc().hour(9)`, i.e. the digits pinned to an arbitrary date. Only the UTC hour/minute
 * carry meaning, so read them with `dayjs.utc()` and never convert.
 */
const legacyDateToMinutes = (value) => {
    if (!value) return null;
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) return null;
    return parsed.hour() * 60 + parsed.minute();
};

/**
 * Collapses either storage format into minutes past midnight, so callers never have to know
 * whether a company was configured before or after the timezone fix.
 *
 * @returns {{ startMinutes: number, endMinutes: number } | null}
 */
export const normalizeOperationalHours = (operationalHours) => {
    if (!operationalHours) return null;

    const startMinutes = isValidMinutes(operationalHours.startMinutes)
        ? operationalHours.startMinutes
        : legacyDateToMinutes(operationalHours.startTime);
    const endMinutes = isValidMinutes(operationalHours.endMinutes)
        ? operationalHours.endMinutes
        : legacyDateToMinutes(operationalHours.endTime);

    if (startMinutes === null || endMinutes === null) return null;

    return { startMinutes, endMinutes };
};

/**
 * True when `minutes` falls inside the window. Handles windows that wrap past midnight
 * (e.g. 18:00 → 02:00), which a naive `start <= x <= end` check would reject entirely.
 *
 * @param {number} minutes
 * @param {{ startMinutes: number, endMinutes: number }} hours
 */
export const isMinuteWithinWindow = (minutes, hours) => {
    if (hours.endMinutes < hours.startMinutes) {
        return minutes >= hours.startMinutes || minutes <= hours.endMinutes;
    }
    return minutes >= hours.startMinutes && minutes <= hours.endMinutes;
};

/** Minutes past midnight of `instant` as read on the given clock. */
export const instantToCompanyMinutes = (instant, timeZone) => {
    const zone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_COMPANY_TIMEZONE;
    const local = dayjs(instant).tz(zone);
    if (!local.isValid()) return null;
    return local.hour() * 60 + local.minute();
};

/**
 * Which opening window applies to a booking, and on whose clock. Mirrors the backend's
 * `resolveBookingHours` and the booking frontend's copy, so the dashboard locks exactly the
 * times the server would reject:
 *
 *  1. `preferences.enableOperationalHours` is the company-level master switch.
 *  2. The window is the location's when it has `enabled` set, otherwise the company's.
 *  3. The clock is the location's, falling back to the company's — never guessed.
 *
 * @returns {{ hours: { startMinutes: number, endMinutes: number }, timeZone: string } | null}
 *   null when no window applies, i.e. "do not restrict anything".
 */
export const resolveBookingHours = (company, location) => {
    if (!company?.preferences?.enableOperationalHours) return null;

    const locationHours = location?.operationalHours?.enabled
        ? normalizeOperationalHours(location.operationalHours)
        : null;
    const hours = locationHours ?? normalizeOperationalHours(company?.operationalHours);
    if (!hours) return null;

    const timeZone = resolveBookingTimeZoneStrict(company, location);
    if (!timeZone) return null;

    return { hours, timeZone };
};

/**
 * Builds the dayjs pair the antd RangePicker binds to.
 *
 * The picker only ever reads hour/minute off these, so they are anchored to an arbitrary day —
 * what matters is that the digits come straight from the stored minutes without passing through
 * any timezone conversion.
 */
export const operationalHoursToPickerValue = (operationalHours) => {
    const normalized = normalizeOperationalHours(operationalHours);
    if (!normalized) return null;

    const dayStart = dayjs().startOf("day");
    return [dayStart.add(normalized.startMinutes, "minute"), dayStart.add(normalized.endMinutes, "minute")];
};

/** Converts the RangePicker's value back into the minutes payload the API expects. */
export const pickerValueToOperationalHours = (value) => {
    if (!Array.isArray(value) || value.length !== 2) return null;

    const [start, end] = value;
    if (!start?.isValid?.() || !end?.isValid?.()) return null;

    return {
        startMinutes: start.hour() * 60 + start.minute(),
        endMinutes: end.hour() * 60 + end.minute(),
    };
};

/** @param {number} minutes */
export const minutesToLabel = (minutes) => {
    const normalized = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    return dayjs().startOf("day").add(normalized, "minute").format("h:mm A");
};

/**
 * Formats a stored UTC instant on the *company's* clock.
 *
 * @param {Date | string | null | undefined} instant
 * @param {string} timeZone
 * @param {string} template
 */
export const formatInCompanyTimeZone = (instant, timeZone, template) => {
    if (!instant) return "";
    const parsed = dayjs(instant).tz(isValidTimeZone(timeZone) ? timeZone : DEFAULT_COMPANY_TIMEZONE);
    return parsed.isValid() ? parsed.format(template) : "";
};

/** Short zone label (e.g. "CDT") for showing admins which clock they are looking at. */
export const timeZoneAbbreviation = (timeZone) => {
    if (!isValidTimeZone(timeZone)) return "";
    try {
        return (
            new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
                .formatToParts(new Date())
                .find((part) => part.type === "timeZoneName")?.value ?? ""
        );
    } catch {
        return "";
    }
};

/**
 * The timezone the dashboard renders booking times in.
 *
 * Reservation instants are absolute, so *something* has to choose the clock they are displayed
 * on. Rendering them on each admin's browser clock (the previous behaviour) means a manager
 * working remotely sees different times than the branch does — the same 9 AM pickup reads as
 * 7 PM from Dhaka, and appears to fall outside the operational hours that were set for it.
 * Pinning the whole dashboard to the company's clock makes every admin see the counter's time.
 *
 * Held at module scope rather than threaded through props because the ~30 existing call sites
 * read these formatters without any company in hand; `UserContext` sets it once, on load.
 */
let activeCompanyTimeZone = DEFAULT_COMPANY_TIMEZONE;

/** @param {{ timezone?: string, address?: { state?: string, country?: string } } | null | undefined} company */
export const setActiveCompanyTimeZone = (company) => {
    activeCompanyTimeZone = resolveCompanyTimeZone(company);
};

export const getActiveCompanyTimeZone = () => activeCompanyTimeZone;

/**
 * Suggests a timezone for a state, for prefilling a location's zone. Only ever a suggestion —
 * split states (El Paso is Mountain while the rest of Texas is Central) and non-US branches are
 * exactly why the field is editable.
 *
 * @param {string | null | undefined} state
 */
export const suggestTimeZoneForState = (state) => timeZoneFromAddress({ state, country: "US" });

/** The distinct zones US operators can be in, for a location's timezone select. */
export const US_TIMEZONE_OPTIONS = [
    { value: "America/New_York", label: "Eastern — America/New_York" },
    { value: "America/Chicago", label: "Central — America/Chicago" },
    { value: "America/Denver", label: "Mountain — America/Denver" },
    { value: "America/Phoenix", label: "Mountain, no DST — America/Phoenix" },
    { value: "America/Los_Angeles", label: "Pacific — America/Los_Angeles" },
    { value: "America/Anchorage", label: "Alaska — America/Anchorage" },
    { value: "Pacific/Honolulu", label: "Hawaii — Pacific/Honolulu" },
];

/**
 * Grouped options for a business timezone select (company or location): the US zones first, then
 * every other zone the browser knows, so an operator outside the US can still name their clock.
 * `current` is always included, so a saved value outside both lists still shows instead of blank.
 */
export const buildTimeZoneSelectOptions = (current, labels = {}) => {
    const usValues = new Set(US_TIMEZONE_OPTIONS.map((option) => option.value));
    let allZones = [];
    try {
        if (typeof Intl.supportedValuesOf === "function") allZones = Intl.supportedValuesOf("timeZone");
    } catch {
        allZones = [];
    }
    const others = allZones
        .filter((zone) => !usValues.has(zone))
        .map((zone) => ({ value: zone, label: zone.replace(/_/g, " ") }));
    if (current && !usValues.has(current) && !others.some((option) => option.value === current)) {
        others.unshift({ value: current, label: current });
    }
    return [
        { label: labels.us || "United States", options: US_TIMEZONE_OPTIONS },
        ...(others.length ? [{ label: labels.other || "Other timezones", options: others }] : []),
    ];
};
